// src/pages/Admin/TestPeriod.jsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import { DayPicker } from "react-day-picker";
import { ko } from "date-fns/locale";
import "react-day-picker/dist/style.css";
import "../../../styles/period.css";

const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

const formatKstDate = (date) => {
    if (!date) return "-";
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, "0");
    const d = `${date.getDate()}`.padStart(2, "0");
    return `${y}.${m}.${d}`;
};
const baseBtn =
    "flex-1 h-10 rounded-2xl border text-sm transition-colors";

const disabledStyle =
    "border-[#E2E4F0] bg-white text-[#B0B4C3] cursor-not-allowed";

const idleStyle =
    "border-[#E2E4F0] bg-white text-[#6B7280]";

const activeStyle =
    "border-[#7864F9] bg-[#F2F0FF] text-[#7864F9]";


const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const TestPeriod = () => {
    const [range, setRange] = useState({ from: undefined, to: undefined });
    const [month, setMonth] = useState(new Date());
    const [repeatType, setRepeatType] = useState(null);

    // ✅ 한 번이라도 from & to 를 모두 선택했는지 여부
    const [hasRangeEverCompleted, setHasRangeEverCompleted] = useState(false);
    const today = stripTime(new Date());

    const bottomPanelRef = useRef(null);

    // from + to 가 모두 채워지는 순간 true 로 고정
    useEffect(() => {
        if (range.from && range.to) {
            setHasRangeEverCompleted(true);
        }
    }, [range.from, range.to]);



    const handleDayClick = useCallback(
        (day) => {
            const clicked = stripTime(day);           // 시간 제거
            const { from, to } = range;
            const fromDate = from ? stripTime(from) : undefined;

            // 🔹 0. 시작/종료 공통: 오늘 이전 날짜는 전부 선택 불가
            if (clicked < today) return;

            // 🔹 1) 아무것도 선택 안 된 상태 → 첫 클릭 = 시작일 (오늘 포함 이후만)
            if (!from && !to) {
                setRange({ from: clicked, to: undefined });
                return;
            }

            // 🔹 2) 시작일만 있고 종료일 없는 상태 (두 번째 클릭)
            if (from && !to) {
                // 2-1) 같은 날짜 재클릭 → 당일 1일 기간
                if (fromDate && isSameDay(clicked, fromDate)) {
                    setRange({ from: fromDate, to: clicked });
                    return;
                }

                // 2-2) 종료일은 시작일보다 이후도, 이전도 가능하지만
                //      반드시 "오늘 포함 이후"여야만 함 (위에서 이미 체크)
                if (fromDate) {
                    if (clicked >= fromDate) {
                        // 시작일보다 같거나 이후 → 그대로 종료일
                        setRange({ from: fromDate, to: clicked });
                    } else {
                        // 시작일보다 이전 → 시작/종료 서로 바꿈
                        setRange({ from: clicked, to: fromDate });
                    }
                }
                return;
            }

            // 🔹 3) 시작/종료가 모두 있는 상태 (세 번째 클릭부터)
            //      → 항상 "새 시작일"로 리셋 (오늘 포함 이후만, 위에서 이미 검증)
            setRange({ from: clicked, to: undefined });
        },
        [range]
    );


    const canApply = !!(range.from && range.to);

    const handleApply = () => {
        if (!canApply) return;
        alert(
            `선택된 기간: ${formatKstDate(range.from)} ~ ${formatKstDate(
                range.to
            )}`
        );
    };

    const handleReset = () => {
        setRange({ from: undefined, to: undefined });
        setHasRangeEverCompleted(false); // ✅ 초기화 시에는 다시 완전 숨김
    };

    // DayPicker 에 줄 range 하이라이트용 modifier들
    // DayPicker 에 줄 range 하이라이트용 modifier들
    const modifiers = {
        selectedRange:
            range.from && range.to ? { from: range.from, to: range.to } : undefined,
        rangeStart: range.from,
        rangeEnd: range.to,

        // 기간 안 + 일요일
        rangeSunday: (day) => {
            if (!range.from || !range.to) return false;
            const s = stripTime(range.from);
            const e = stripTime(range.to);
            const d = stripTime(day);
            return d >= s && d <= e && d.getDay() === 0;
        },

        // 기간 안 + 토요일
        rangeSaturday: (day) => {
            if (!range.from || !range.to) return false;
            const s = stripTime(range.from);
            const e = stripTime(range.to);
            const d = stripTime(day);
            return d >= s && d <= e && d.getDay() === 6;
        },

        // 🔹 기간 안 + 해당 월의 1일
        rangeMonthFirst: (day) => {
            if (!range.from || !range.to) return false;
            const s = stripTime(range.from);
            const e = stripTime(range.to);
            const d = stripTime(day);
            return d >= s && d <= e && d.getDate() === 1;
        },

        // 🔹 기간 안 + 해당 월의 말일
        rangeMonthLast: (day) => {
            if (!range.from || !range.to) return false;
            const s = stripTime(range.from);
            const e = stripTime(range.to);
            const d = stripTime(day);
            const lastDateOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
            return d >= s && d <= e && d.getDate() === lastDateOfMonth;
        },
    };


    const hasFullRange = !!(range.from && range.to);
    const showBottomPanel = hasRangeEverCompleted || hasFullRange;

    // ✅ 시작+종료일이 모두 선택된 순간 알림 반복 영역으로 스크롤
useEffect(() => {
    if (hasFullRange && bottomPanelRef.current) {
        bottomPanelRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }
}, [hasFullRange]);  // from/to가 둘 다 채워진 시점에만

    return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
            <div className="bg-white shadow-lg rounded-2xl p-6 w-full max-w-md">
                <h2 className="text-base font-semibold text-neutral-900 mb-4">
                    기간 선택 테스트 (TestPeriod)
                </h2>

                {/* 달력 */}
                <div className="border border-neutral-200 rounded-xl p-4 bg-neutral-50/70">
                    <DayPicker
                        mode="default"
                        locale={ko}
                        month={month}              // ✅ month 상태 사용
                        onMonthChange={setMonth}   // ✅ 화살표로 month 변경
                        selected={undefined}
                        disabled={{ before: today }} 
                        onDayClick={handleDayClick}
                        numberOfMonths={1}
                        weekStartsOn={0}
                        modifiers={modifiers}
                        modifiersClassNames={{
                            selectedRange: "rdp-selected-range",
                            rangeStart: "rdp-range-start",
                            rangeEnd: "rdp-range-end",
                            rangeSunday: "rdp-range-sunday",
                            rangeSaturday: "rdp-range-saturday",
                            rangeMonthFirst: "rdp-range-month-first",
                            rangeMonthLast: "rdp-range-month-last",
                        }}
                    />

                </div>

                {/* 🔹 시작일 / 종료일 요약 (달력 바로 아래, showBottomPanel 밖) */}

                <div className={`mt-4`}>
                    <div className="w-full rounded-2xl border border-[#D7DAE8] bg-white px-6 py-3 flex items-center justify-between">
                        {/* 시작일 */}
                        <div className="flex-1 text-center">
                            <div className="text-xs text-[#6B7280] mb-1">시작일</div>
                            <div className="text-sm font-semibold text-[#5950FF]">
                                {formatKstDate(range.from)}
                            </div>
                        </div>

                        {/* ~ 기호 */}
                        <div className="px-2 text-[#5950FF] font-semibold">~</div>

                        {/* 종료일 */}
                        <div className="flex-1 text-center">
                            <div className="text-xs text-[#6B7280] mb-1">종료일</div>
                            <div className="text-sm font-semibold text-[#5950FF]">
                                {formatKstDate(range.to)}
                            </div>
                        </div>
                    </div>
                </div>


                {/* 🔹 알림 반복 영역만 showBottomPanel으로 제어 */}
                {showBottomPanel && (
                    <div ref={bottomPanelRef} className={`mt-6 ${!hasFullRange ? "opacity-60" : ""}`}>
                        <div className="mb-1 text-sm font-semibold text-neutral-900">
                            알림 반복
                        </div>
                        <div className="mb-4 text-xs text-neutral-500">
                            선택한 항목으로 알림이 반복됩니다.
                        </div>

                        {/* 버튼 3개 (시간 / 요일 / 날짜) */}
                        <div className="flex gap-2">
                            {/* 시간 */}
                            <button
                                type="button"
                                onClick={() => hasFullRange && setRepeatType("time")}
                                disabled={!hasFullRange}
                                className={`${baseBtn} ${!hasFullRange
                                    ? disabledStyle
                                    : repeatType === "time"
                                        ? activeStyle
                                        : idleStyle
                                    }`}
                            >
                                시간
                            </button>

                            {/* 요일 */}
                            <button
                                type="button"
                                onClick={() => hasFullRange && setRepeatType("weekday")}
                                disabled={!hasFullRange}
                                className={`${baseBtn} ${!hasFullRange
                                    ? disabledStyle
                                    : repeatType === "weekday"
                                        ? activeStyle
                                        : idleStyle
                                    }`}
                            >
                                요일
                            </button>

                            {/* 날짜 */}
                            <button
                                type="button"
                                onClick={() => hasFullRange && setRepeatType("date")}
                                disabled={!hasFullRange}
                                className={`${baseBtn} ${!hasFullRange
                                    ? disabledStyle
                                    : repeatType === "date"
                                        ? activeStyle
                                        : idleStyle
                                    }`}
                            >
                                날짜
                            </button>
                        </div>


                        {/* 적용 / 초기화 버튼 + 안내 문구는 그대로 유지하고 싶으면 아래처럼 */}
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={handleReset}
                                className="px-3 h-9 rounded-full border border-neutral-300 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
                            >
                                초기화
                            </button>
                            <button
                                type="button"
                                onClick={handleApply}
                                disabled={!canApply}
                                className={`px-4 h-9 rounded-full text-xs font-medium ${canApply
                                    ? "bg-[#7864F9] text-white hover:bg-[#6750F5]"
                                    : "bg-[#7864F91A] text-[#7864F9] cursor-not-allowed"
                                    }`}
                            >
                                적용
                            </button>
                        </div>

                        <p className="mt-3 text-[11px] text-neutral-500">
                            ※ 시작일과 종료일이 모두 선택되어야만 적용 버튼이 활성화됩니다.
                            <br />
                            (3번째 클릭부터는 항상 새 시작일로 다시 선택)
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TestPeriod;
